import { SITE_URL } from "@/components/SEO";
import { ORG_APPLY_URL } from "@/lib/organisationSignup";

const organization = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Privacy360",
  alternateName: "Privacy360 by Formiti",
  legalName: "Privacy360 Limited",
  url: SITE_URL,
  logo: `${SITE_URL}/logo-512.png`,
  description:
    "Privacy360 is a global privacy, AI governance, and DPO operations platform built by Formiti Data International.",
  parentOrganization: {
    "@type": "Organization",
    name: "Formiti Data International UK Ltd",
    url: "https://formiti.com",
    sameAs: ["https://www.linkedin.com/company/formiti-data-international/"],
  },
  sameAs: [
    "https://www.linkedin.com/company/privacy360/",
    "https://formiti.com",
  ],
};

const website = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "Privacy360",
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-GB",
};

const softwareApplication = {
  "@type": "SoftwareApplication",
  "@id": `${SITE_URL}/#software`,
  name: "Privacy360",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web browser",
  url: SITE_URL,
  publisher: { "@id": `${SITE_URL}/#organization` },
  description:
    "Privacy360 is an operational platform for privacy management, AI governance, consent management, contract review, training and audit-ready evidence.",
  featureList: [
    "Privacy assessments",
    "ROPA and processor records",
    "DSAR and breach workflows",
    "AI system register and AI assessments",
    "Consent management",
    "Contract review",
    "Training and evidence management",
    "Multilingual workflow delivery",
  ],
  offers: {
    "@type": "AggregateOffer",
    name: "Privacy360 platform plans",
    lowPrice: "495",
    highPrice: "1950",
    priceCurrency: "GBP",
    offerCount: "3",
    url: `${SITE_URL}/pricing`,
    offers: [
      { "@type": "Offer", name: "Core Privacy", price: "495", priceCurrency: "GBP", url: `${SITE_URL}/pricing` },
      { "@type": "Offer", name: "Professional", price: "995", priceCurrency: "GBP", url: `${SITE_URL}/pricing` },
      { "@type": "Offer", name: "Enterprise", price: "1950", priceCurrency: "GBP", url: `${SITE_URL}/pricing` },
    ],
  },
};

export const buildWebPageSchema = (path: string, name: string, description: string, extra: Record<string, unknown> = {}) => ({
  "@type": "WebPage",
  "@id": `${SITE_URL}${path}#webpage`,
  url: `${SITE_URL}${path}`,
  name,
  description,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  about: { "@id": `${SITE_URL}/#software` },
  inLanguage: "en-GB",
  ...extra,
});

export const baseSiteSchema = [organization, website, softwareApplication];

export const organisationSignupOfferSchema = {
  "@type": "Offer",
  "@id": `${SITE_URL}/pricing#organisation-signup`,
  name: "Privacy360 standalone organisation subscription",
  description:
    "Sign up directly for Privacy360: your own branded platform, your own web address and one admin console for every legal entity. Applications are reviewed and approved by our team.",
  priceCurrency: "GBP",
  price: "495",
  availability: "https://schema.org/InStock",
  url: `${SITE_URL}${ORG_APPLY_URL}`,
  itemOffered: { "@id": `${SITE_URL}/#software` },
};

export const buildBreadcrumbSchema = (items: Array<{ name: string; path: string }>) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const buildFaqSchema = (faqs: Array<{ q: string; a: string }>) => ({
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
});

export const buildVideoSchema = (v: {
  name: string;
  description: string;
  thumbnailUrl: string;
  contentUrl: string;
  uploadDate: string;
  durationSeconds: number;
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: v.name,
  description: v.description,
  thumbnailUrl: `${SITE_URL}${v.thumbnailUrl}`,
  contentUrl: `${SITE_URL}${v.contentUrl}`,
  uploadDate: v.uploadDate,
  duration: `PT${v.durationSeconds}S`,
  publisher: organization,
});

export const buildComparePageSchema = (
  path: string,
  name: string,
  description: string,
  lastReviewed: string,
) =>
  buildWebPageSchema(path, name, description, {
    datePublished: lastReviewed,
    dateModified: lastReviewed,
    reviewedBy: { "@id": `${SITE_URL}/#organization` },
  });

export const buildCompareArticleSchema = (
  path: string,
  headline: string,
  description: string,
  datePublished: string,
  dateModified: string = datePublished,
) => ({
  "@type": "Article",
  "@id": `${SITE_URL}${path}#article`,
  headline,
  description,
  url: `${SITE_URL}${path}`,
  mainEntityOfPage: { "@id": `${SITE_URL}${path}#webpage` },
  datePublished,
  dateModified,
  author: { "@type": "Organization", name: "Privacy360 by Formiti" },
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-GB",
});
export const partnerProgramSchema = {
  "@type": "Service",
  "@id": `${SITE_URL}/partners#partner-program`,
  name: "Privacy360 White-label Privacy Partner Program",
  alternateName: [
    "Privacy360 white-label partner program",
    "White-label GDPR compliance platform partner program",
    "White-label privacy management platform for DPOs",
    "Multi-tenant privacy governance software for consultants",
    "White-label privacy compliance software for law firms",
    "MSP white-label privacy compliance platform",
    "White-label Privacy-as-a-Service for MSPs",
    "Rebrandable DPO client portal partner program",
  ],
  serviceType: "White-label privacy platform partner program",
  category: "Reseller and white-label software partner program",
  description:
    "An approval-gated white-label partner program for fractional DPOs and privacy consultants, law firms and legal advisory practices, and MSPs and MSSPs. Partners deliver GDPR, UK GDPR, EU AI Act, India DPDP and CCPA/CPRA programmes to their own clients under their own brand, from a multi-tenant dashboard with a rebrandable client portal, automated DPIA and privacy risk assessment workflows, white-label consent management, isolated client tenants, audited impersonation and per-client data residency.",
  url: `${SITE_URL}/partners`,
  provider: { "@id": `${SITE_URL}/#organization` },
  brand: { "@id": `${SITE_URL}/#organization` },
  isRelatedTo: { "@id": `${SITE_URL}/#software` },
  areaServed: ["GB", "EU", "US", "IN", "AE", "SG", "MY"],
  audience: {
    "@type": "BusinessAudience",
    name: "Fractional DPOs and privacy consultants, law firms and legal advisory practices, MSPs and MSSPs delivering Privacy-as-a-Service",
  },
  termsOfService: `${SITE_URL}/terms-and-conditions`,
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Privacy360 white-label partner tiers",
    itemListElement: [
      {
        "@type": "Offer",
        name: "Starter partner tier",
        description: "Up to 5 managed clients, white-label partner shell and dashboard, white-label client portal.",
        price: "4990",
        priceCurrency: "GBP",
        url: `${SITE_URL}/partners#tiers`,
        eligibleCustomerType: "https://schema.org/Business",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "4990",
          priceCurrency: "GBP",
          billingDuration: 1,
          billingIncrement: 1,
          unitCode: "ANN",
        },
      },
      {
        "@type": "Offer",
        name: "Professional partner tier",
        description:
          "Up to 10 managed clients, white-label branding across all modules, priority support, advanced entity management.",
        price: "999",
        priceCurrency: "GBP",
        url: `${SITE_URL}/partners#tiers`,
        eligibleCustomerType: "https://schema.org/Business",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "999",
          priceCurrency: "GBP",
          billingDuration: 1,
          billingIncrement: 1,
          unitCode: "MON",
        },
      },
      {
        "@type": "Offer",
        name: "Enterprise partner tier",
        description:
          "20+ managed clients, white-label branding across all modules, custom integrations, dedicated account manager.",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "GBP",
          valueAddedTaxIncluded: false,
        },
        url: `${SITE_URL}/partners#tiers`,
        eligibleCustomerType: "https://schema.org/Business",
      },
    ],
  },
};

export const buildHowToSchema = (
  name: string,
  description: string,
  path: string,
  steps: Array<{ title: string; body: string }>,
) => ({
  "@type": "HowTo",
  "@id": `${SITE_URL}${path}#howto`,
  name,
  description,
  totalTime: "P5D",
  step: steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.title,
    text: step.body,
    url: `${SITE_URL}${path}#step-${index + 1}`,
  })),
});
